import React, { useMemo } from "react";
import { Navigate } from "react-router";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { Spin } from "antd";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useSearchParams } from "react-router";

type Props = {
  homePage: string;
};

/**
 * Handles the root URL "/" when SSO is configured.
 * Redirects directly to SSO authorize with state="/" (or empty) without intermediate /sso/no-rights.
 */
const RootRedirect = ({ homePage }: Props) => {
  const authenticated = useAuthStore.getState().authenticated;
  const ssoModule = getModule("sso");
  const isCompanionSso = useMemo(() => ssoModule?.enabled, []);
  const ssoAuthorizeUrl = useMemo(() => ssoModule?.options?.ssoAuthorizeUrl ?? "", []);
  const [searchParams] = useSearchParams();
  const ssoAuthCode = useMemo(() => searchParams.get("code"), [searchParams]);

  const ssoAuthorizeUrlWithRootState = useMemo(() => {
    if (!ssoAuthorizeUrl) return "";
    const state = "/";
    try {
      const url = new URL(ssoAuthorizeUrl);
      url.searchParams.set("state", state);
      url.searchParams.set("redirect_uri", window.location.origin + "/");
      return url.toString();
    } catch {
      const hasParams = ssoAuthorizeUrl.includes("?");
      const separator = hasParams ? "&" : "?";
      const encodedState = encodeURIComponent(state);
      const encodedRedirectUri = encodeURIComponent(window.location.origin + "/");
      return `${ssoAuthorizeUrl}${separator}state=${encodedState}&redirect_uri=${encodedRedirectUri}`;
    }
  }, [ssoAuthorizeUrl]);

  if (
    isCompanionSso &&
    ssoAuthorizeUrl &&
    !authenticated &&
    !ssoAuthCode
  ) {
    window.open(ssoAuthorizeUrlWithRootState, "_self");
    return <Spin className="sso-consent__spin" size="large" />;
  }

  return <Navigate to={`/${homePage}`} />;
};

export default RootRedirect;
